import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { X, Printer, RefreshCw, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { useDesignContext } from '../hooks/useDesignContext';
import { buildApiUrl } from '../config/api';
import { renderProduction300DPI } from '../utils/productionRender';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const DialogContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 100%;
  max-width: 1200px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f9fafb;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  color: #6b7280;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const Content = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const PreviewSection = styled.div`
  flex: 1;
  padding: 12px;
  background: #f8f9fa;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const SettingsSection = styled.div`
  width: 320px;
  background: white;
  border-left: 1px solid #e5e7eb;
  padding: 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 1024px) {
    display: none; // Hide on smaller screens to prioritize preview
  }
`;

const PreviewContainer = styled.div`
  background: white;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const PreviewTopBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`;

const PreviewMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const PreviewMetaTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PreviewMetaSubtitle = styled.div`
  font-size: 12px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PreviewControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const IconButton = styled.button`
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #374151;
  transition: all 0.2s;

  &:hover {
    border-color: #5D722C;
    color: #5D722C;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  }

  &:active {
    transform: translateY(1px);
  }
`;

const ZoomValue = styled.div`
  font-size: 12px;
  color: #6b7280;
  min-width: 52px;
  text-align: center;
  font-variant-numeric: tabular-nums;
`;

const DesignPreview = styled.div<{ $landscape?: boolean }>`
  width: 100%;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  background: #f3f4f6;
  border-radius: 12px;
  margin: 0 auto;
  position: relative;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  aspect-ratio: ${props => props.$landscape ? '11 / 8.5' : '8.5 / 11'};
  max-height: calc(90vh - 260px);
`;

const DesignPreviewInner = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

const DesignImage = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const CompanyFooter = styled.div`
  text-align: center;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
`;

const CompanyLogo = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 4px;
`;

const CompanySubtitle = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const Notes = styled.div`
  margin-top: 20px;
  font-size: 10px;
  color: #6b7280;
  line-height: 1.4;
`;

const PreviewFooter = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  border: 1px dashed #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FooterItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
`;

const FooterLabel = styled.span`
  color: #6b7280;
  font-weight: 500;
`;

const FooterValue = styled.span`
  color: #111827;
  font-weight: 600;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #5D722C;
    box-shadow: 0 0 0 3px rgba(93, 114, 44, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  resize: vertical;
  min-height: 60px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #5D722C;
    box-shadow: 0 0 0 3px rgba(93, 114, 44, 0.1);
  }
`;

const ToggleGroup = styled.div`
  display: flex;
  background: #f3f4f6;
  border-radius: 6px;
  padding: 2px;
`;

const ToggleButton = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.active ? '#5D722C' : 'transparent'};
  color: ${props => props.active ? 'white' : '#6b7280'};

  &:hover {
    background: ${props => props.active ? '#4a5b23' : '#e5e7eb'};
  }
`;

const SliderGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SliderLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: #374151;
  gap: 10px;
`;

const Slider = styled.input`
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #e5e7eb;
  outline: none;
  -webkit-appearance: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #5D722C;
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #5D722C;
    cursor: pointer;
    border: none;
  }
`;

const Actions = styled.div`
  padding: 20px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 12px;
  justify-content: center;
  background: #f9fafb;
`;

const Button = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 120px;
  justify-content: center;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  ${props => props.variant === 'primary' ? `
    background: #5D722C;
    color: white;
    border: none;

    &:hover {
      background: #4a5b23;
    }
  ` : `
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;

    &:hover {
      background: #f9fafb;
    }
  `}
`;

interface PrintPreviewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onPrint: (settings: PrintSettings) => void;
}

interface PrintSettings {
  includePrices: boolean;
  includeSizes: boolean;
  includeCompanyLogo: boolean;
  includeManufacturerNote: boolean;
  includeCopyrightNotice: boolean;
  includePurchaseOrder: boolean;
  to: string;
  poNumber: string;
  material: string;
  message: string;
  orientation: 'portrait' | 'landscape';
  textOverlap: boolean;
  textScale: number;
  printBothSides: boolean;
}

const PrintPreviewDialog: React.FC<PrintPreviewDialogProps> = ({ isOpen, onClose, onPrint }) => {
  const { currentDesign, stageRef, selectedGraniteMeta, printSettings, setPrintSettings, activeSide, setActiveSide, sharedColors, fetchModelDetails } = useDesignContext();
  const [modelDetails, setModelDetails] = useState<any>(null);
  const [esqModalDetails, setEsqModalDetails] = useState<any>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewBackImage, setPreviewBackImage] = useState<string | null>(null);
  const [previewZoom, setPreviewZoom] = useState(1);
  const hasCapturedRef = useRef(false);
  const isCapturingRef = useRef(false);

  const isBackSideSupported = useMemo(() => {
    const modelName = String(currentDesign?.selectedModelName || '').toLowerCase();
    const modelId = Number(currentDesign?.selectedModelId || 0);
    const sizeName = String(currentDesign?.selectedSizeName || '').replace(/\s+/g, '');

    // Serptop sizes match logic
    if (modelId === 3 || modelName.includes('serptop')) {
      const supportedSizes = [
        '24x6x24/36x12x6',
        '36x6x24/48x12x6',
        '48x6x24/66x12x6',
        '54x8x28/72x14x8'
      ];
      return supportedSizes.some(s => {
        const diePart = s.split('/')[0].replace(/\s+/g, '');
        return sizeName.includes(diePart);
      });
    }

    // Tear Drop
    if (modelId === 5 || modelName.includes('tear drop') || modelName.includes('teardrop')) {
      const targetSize = '24x6x28/36x12x6'.split('/')[0].replace(/\s+/g, '');
      return sizeName.includes(targetSize);
    }

    // Single heart
    if (modelId === 4 || modelName.includes('single heart')) {
      const targetSize = '24x6x28/36x12x6'.split('/')[0].replace(/\s+/g, '');
      return sizeName.includes(targetSize);
    }

    return false;
  }, [currentDesign?.selectedModelName, currentDesign?.selectedModelId, currentDesign?.selectedSizeName]);

  const cropTransparentMargins = useCallback(async (dataUrl: string, padding = 12): Promise<string> => {
    try {
      const img = new Image();
      img.src = dataUrl;
      await img.decode();

      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return dataUrl;

      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const { data, width, height } = imageData;

      let minX = width;
      let minY = height;
      let maxX = -1;
      let maxY = -1;

      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const idx = (y * width + x) * 4;
          const alpha = data[idx + 3];
          if (alpha > 0) {
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
          }
        }
      }

      if (maxX < 0 || maxY < 0) return dataUrl;

      minX = Math.max(0, minX - padding);
      minY = Math.max(0, minY - padding);
      maxX = Math.min(width - 1, maxX + padding);
      maxY = Math.min(height - 1, maxY + padding);

      const cropW = maxX - minX + 1;
      const cropH = maxY - minY + 1;

      const out = document.createElement('canvas');
      out.width = cropW;
      out.height = cropH;
      const outCtx = out.getContext('2d');
      if (!outCtx) return dataUrl;

      outCtx.drawImage(canvas, minX, minY, cropW, cropH, 0, 0, cropW, cropH);
      return out.toDataURL('image/png');
    } catch {
      return dataUrl;
    }
  }, []);

  const isAuthed = (() => {
    try { return Boolean(localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token')); } catch { return false; }
  })();

  // Capture design preview at 300 DPI
  const captureDesignPreview = useCallback(async () => {
    if (!stageRef?.current) return;

    const graniteConfig = (window as any).__appGraniteConfig;
    const designMode = (window as any).__appDesignMode || '';
    const elements = currentDesign.elements;

    const dataURL = await renderProduction300DPI(
      stageRef.current,
      graniteConfig,
      designMode,
      elements,
      { format: 'png', quality: 1, targetDPI: 300 }
    );

    const cropped = await cropTransparentMargins(dataURL, 12);
    return cropped;
  }, [stageRef, currentDesign.elements, cropTransparentMargins]);

  // Capture preview when dialog opens
  useEffect(() => {
    if (isOpen) {
      setPreviewZoom(1);
      if (!hasCapturedRef.current && !isCapturingRef.current) {
        hasCapturedRef.current = true;
        isCapturingRef.current = true;

        const runCapture = async () => {
          try {
            if (isBackSideSupported) {
              // Dual side capture
              const initialSide = activeSide;
              const otherSide = initialSide === 'front' ? 'back' : 'front';

              // Capture current side
              const img1 = await captureDesignPreview();

              // Switch side
              setActiveSide(otherSide);
              await new Promise(r => setTimeout(r, 600)); // Wait for render

              // Capture other side
              const img2 = await captureDesignPreview();

              // Restore side
              setActiveSide(initialSide);

              if (initialSide === 'front') {
                setPreviewImage(img1 || null);
                setPreviewBackImage(img2 || null);
              } else {
                setPreviewImage(img2 || null);
                setPreviewBackImage(img1 || null);
              }
            } else {
              // Single side capture
              const img = await captureDesignPreview();
              setPreviewImage(img || null);
              setPreviewBackImage(null);
            }
          } finally {
            isCapturingRef.current = false;
          }
        };

        runCapture();
      }
    } else {
      hasCapturedRef.current = false;
      isCapturingRef.current = false;
      setPreviewImage(null);
      setPreviewBackImage(null);
    }
  }, [isOpen, captureDesignPreview, isBackSideSupported, activeSide, setActiveSide]);

  // Fetch model details
  useEffect(() => {
    if (!currentDesign.selectedModelId) return;

    const fetchModelDetailsLocal = async () => {
      try {
        const details = await fetchModelDetails(currentDesign.selectedModelId as number);
        setModelDetails(details);
      } catch (err) {
      }
    };

    fetchModelDetailsLocal();
  }, [currentDesign.selectedModelId]);

  // Fetch ESQ modal details for accurate ESQ print metadata (name/dimensions/price).
  useEffect(() => {
    const getEsqMeta = () => {
      const els = Array.isArray((currentDesign as any)?.elements) ? (currentDesign as any).elements : [];
      const tpl = els.find((el: any) => el?.type === 'artwork' && (el?.properties?.role === 'template' || el?.properties?.isTemplate));
      const p = tpl?.properties || {};
      const mode = String((currentDesign as any)?.designMode || p?.designMode || '').toUpperCase();
      const modalId = Number((currentDesign as any)?.selectedEsqModalId || p?.esqModalId || (currentDesign as any)?.selectedModelId || 0);
      return { mode, modalId };
    };

    const { mode, modalId } = getEsqMeta();
    if (mode !== 'ESQ' || modalId <= 0) {
      setEsqModalDetails(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(buildApiUrl(`/api/esq/modal/${modalId}`), { headers: { Accept: 'application/json' } });
        const data = await res.json().catch(() => null);
        if (!cancelled && res.ok && data) setEsqModalDetails(data);
      } catch {
        if (!cancelled) setEsqModalDetails(null);
      }
    })();

    return () => { cancelled = true; };
  }, [currentDesign]);

  if (!isOpen) return null;

  const handlePrint = () => {
    if (!isAuthed) return;
    onPrint(printSettings);
  };

  const esqTemplateProps = (() => {
    const els = Array.isArray((currentDesign as any)?.elements) ? (currentDesign as any).elements : [];
    const tpl = els.find((el: any) => el?.type === 'artwork' && (el?.properties?.role === 'template' || el?.properties?.isTemplate));
    return tpl?.properties || {};
  })();
  const isEsqMode = String((currentDesign as any)?.designMode || esqTemplateProps?.designMode || '').toUpperCase() === 'ESQ';

  // Get design details
  const modelName = isEsqMode
    ? (esqModalDetails?.name || esqTemplateProps?.esqModalName || currentDesign.selectedModelName || (currentDesign.selectedModelId ? `ESQ #${currentDesign.selectedModelId}` : 'ESQ'))
    : (modelDetails?.model?.name || (currentDesign.selectedModelId ? `Model #${currentDesign.selectedModelId}` : 'No Model Selected'));
  const selectedColorId = Number((currentDesign as any)?.selectedColorId ?? selectedGraniteMeta?.colorId ?? 0);
  const colorName = (() => {
    const explicit = String(currentDesign.selectedColorName || '').trim();
    if (explicit) return explicit;
    const found = sharedColors.find(c => Number(c.id) === selectedColorId);
    if (found) return found.name || found.code;
    return 'No Color Selected';
  })();
  const esqWidthIn = Number(esqModalDetails?.width_in ?? esqTemplateProps?.esqModalWidthIn ?? (currentDesign as any)?.esqModalWidthIn ?? 0);
  const esqHeightIn = Number(esqModalDetails?.height_in ?? esqTemplateProps?.esqModalHeightIn ?? (currentDesign as any)?.esqModalHeightIn ?? 0);
  const dimensionsLabel = isEsqMode
    ? ((esqWidthIn > 0 && esqHeightIn > 0) ? `${esqWidthIn}" x ${esqHeightIn}"` : (currentDesign.selectedSizeName || 'Not specified'))
    : (currentDesign.selectedSizeName || 'Not specified');
  const resolvedPrice = (() => {
    const modalPrice = Number(esqModalDetails?.price);
    if (isEsqMode && Number.isFinite(modalPrice) && modalPrice > 0) return modalPrice;
    const designPrice = Number((currentDesign as any)?.price);
    if (Number.isFinite(designPrice) && designPrice > 0) return designPrice;
    return null;
  })();
  const designName = currentDesign.name || 'Untitled Design';

  return (
    <Overlay className="print-preview-overlay">
      <DialogContainer className="print-preview-dialog" onClick={(e) => e.stopPropagation()}>
        <Header className="print-preview-dialog-header">
          <Title>
            <Printer size={20} />
            Print Preview
          </Title>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </Header>

        <Content className="print-preview-dialog-content">
          <PreviewSection className="print-preview-preview-section">
            <PreviewContainer className="print-preview-preview-container">
              <PreviewTopBar>
                <PreviewMeta>
                  <PreviewMetaTitle>{designName}</PreviewMetaTitle>
                  <PreviewMetaSubtitle>{modelName}{colorName ? ` • ${colorName}` : ''}</PreviewMetaSubtitle>
                </PreviewMeta>
                <PreviewControls>
                  <IconButton onClick={() => {
                    hasCapturedRef.current = false;
                    // Trigger effect
                    setPreviewImage(null);
                  }} title="Refresh preview">
                    <RefreshCw size={16} />
                  </IconButton>
                  <IconButton
                    onClick={() => setPreviewZoom((z) => Math.max(0.5, Math.round((z - 0.1) * 10) / 10))}
                    title="Zoom out"
                  >
                    <ZoomOut size={16} />
                  </IconButton>
                  <ZoomValue>{Math.round(previewZoom * 100)}%</ZoomValue>
                  <IconButton
                    onClick={() => setPreviewZoom((z) => Math.min(5, Math.round((z + 0.1) * 10) / 10))}
                    title="Zoom in"
                  >
                    <ZoomIn size={16} />
                  </IconButton>
                  <IconButton onClick={() => setPreviewZoom(1)} title="Reset zoom">
                    <Maximize2 size={16} />
                  </IconButton>
                </PreviewControls>
              </PreviewTopBar>

              <DesignPreview $landscape={printSettings?.orientation ? printSettings.orientation === 'landscape' : true}>
                <DesignPreviewInner>
                  <DesignImage style={{ display: 'flex', flexDirection: 'row', gap: '20px', alignItems: 'center' }}>
                    {previewImage ? (
                      <>
                        <div style={{ flex: 1, height: '100%', position: 'relative' }}>
                          <img
                            src={previewImage}
                            alt="Front Preview"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              borderRadius: '10px',
                              transform: `scale(${previewZoom})`,
                              transformOrigin: 'center center'
                            }}
                          />
                          {previewBackImage && printSettings.printBothSides && <div style={{ position: 'absolute', bottom: 5, left: 0, width: '100%', textAlign: 'center', fontSize: '10px', color: '#666' }}>Front</div>}
                        </div>
                        {previewBackImage && printSettings.printBothSides && (
                          <div style={{ flex: 1, height: '100%', position: 'relative' }}>
                            <img
                              src={previewBackImage}
                              alt="Back Preview"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                borderRadius: '10px',
                                transform: `scale(${previewZoom})`,
                                transformOrigin: 'center center'
                              }}
                            />
                            <div style={{ position: 'absolute', bottom: 5, left: 0, width: '100%', textAlign: 'center', fontSize: '10px', color: '#666' }}>Back</div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{
                        width: '220px',
                        height: '160px',
                        background: '#e5e7eb',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#6b7280',
                        fontSize: '14px'
                      }}>
                        {null}
                      </div>
                    )}
                  </DesignImage>
                </DesignPreviewInner>
              </DesignPreview>

              {(printSettings.includePrices || printSettings.includeSizes) && (
                <PreviewFooter className="print-preview-footer">
                  {printSettings.includePrices && (
                    <FooterItem>
                      <FooterLabel>List Price:</FooterLabel>
                      <FooterValue>
                        {resolvedPrice != null ? `$${Number(resolvedPrice).toFixed(2)}` : 'TBD'}
                      </FooterValue>
                    </FooterItem>
                  )}
                  {printSettings.includeSizes && (
                    <FooterItem>
                      <FooterLabel>Dimensions:</FooterLabel>
                      <FooterValue>{dimensionsLabel}</FooterValue>
                    </FooterItem>
                  )}
                </PreviewFooter>
              )}
            </PreviewContainer>
          </PreviewSection>

          <SettingsSection className="print-preview-settings-section">
            <PreviewMetaTitle style={{ marginBottom: '16px' }}>Print Settings</PreviewMetaTitle>

            {isBackSideSupported && (
              <InputGroup>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>Sides to Print</div>
                <ToggleGroup>
                  <ToggleButton
                    active={!printSettings.printBothSides}
                    onClick={() => setPrintSettings({ ...printSettings, printBothSides: false })}
                  >
                    Front Only
                  </ToggleButton>
                  <ToggleButton
                    active={printSettings.printBothSides}
                    onClick={() => setPrintSettings({ ...printSettings, printBothSides: true })}
                  >
                    Both Sides
                  </ToggleButton>
                </ToggleGroup>
              </InputGroup>
            )}

            <InputGroup>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>Order Details</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    id="includePrices"
                    checked={printSettings.includePrices}
                    onChange={(e) => setPrintSettings({ ...printSettings, includePrices: e.target.checked })}
                  />
                  <label htmlFor="includePrices" style={{ fontSize: '14px', color: '#4b5563' }}>Include Pricing</label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    id="includeSizes"
                    checked={printSettings.includeSizes}
                    onChange={(e) => setPrintSettings({ ...printSettings, includeSizes: e.target.checked })}
                  />
                  <label htmlFor="includeSizes" style={{ fontSize: '14px', color: '#4b5563' }}>Include Dimensions</label>
                </div>
              </div>
            </InputGroup>
          </SettingsSection>
        </Content>

        <Actions className="print-preview-actions">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button variant="primary" onClick={handlePrint} disabled={!isAuthed}>
            <Printer size={16} />
            Print
          </Button>
        </Actions>
      </DialogContainer>
    </Overlay>
  );
};

export default PrintPreviewDialog;
